/* eslint-disable max-lines -- not split yet: catalog-channel lock sync, index filters and getTireInventoryFilterSearch are separable */
import {
  CarFiltersListApiResponse,
  EnergyClassEU,
  type ShopSupplierListApiResponse,
  ShopTireBrandListApiResponse,
  ShopTireInventoryDimensionsListApiData,
  ShopTireInventoryDimensionsListApiResponse,
  SoundClass,
  SoundClassLabels,
  SupplierProductStatus,
  SupplierWarehouseListApiResponse,
  TireClassEU,
  TireClassEULabels,
  TireLoadIndex,
  TireLoadIndexLabels,
  type TireSeason,
  TireSpeedIndex,
  TireSpeedIndexLabels,
  WetGripClass,
  WetGripClassLabels
} from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { URLKeys } from 'noddi-async';
import {
  enrichFiltersWithCounts,
  FilterControl,
  type FilterLinkOf,
  FilterOption,
  type FilterOptionSource,
  filterSearch,
  FilterSpec,
  filterUrlState,
  mapFilterControls,
  useFilterUnits,
  usePagination
} from 'noddi-ui-web';
import {
  formatConstantCaseToSentenceCase,
  INVENTORY_CATALOG_CHANNEL_OPTIONS,
  SUPPLIER_PRODUCT_STATUS_OPTIONS,
  TIRE_CLASSIFICATION_OPTIONS,
  TIRE_SEASON_OPTIONS
} from 'noddi-util';

import { useVehicleCatalogNodeOptions } from '../Car/useVehicleCatalogNodeFilter';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';
import { singleWithExclusions } from './inventoryCompositeFilters';
import {
  type CatalogChannelLock,
  isCatalogChannelFilterHidden,
  lockedCatalogChannelQuery
} from './lockedCatalogChannelQuery';
import { tireAxleFitmentsFilter } from './tireAxleFitmentsFilter';
import type { TireInventoryFilterInput } from './tireInventoryFilterUnit';
import { tireInventoryFilterUnit } from './tireInventoryFilterUnit';

type InitialTireInventoryFilters = {
  dimensionWidthMm?: number[];
  dimensionProfile?: number[];
  dimensionRimInch?: number[];
  season?: TireSeason[];
  inStock?: boolean;
  supplierProductStatuses?: SupplierProductStatus[];
};

const INCH_TO_MM_CONVERSION = 25.4;

const TIRE_EU_CLASS_FILTER_OPTIONS: FilterOption<TireClassEU>[] = Object.values(TireClassEU).map((value) => ({
  label: `${value} - ${TireClassEULabels[value]}`,
  value
}));

const TIRE_SOUND_CLASS_FILTER_OPTIONS: FilterOption<SoundClass>[] = Object.values(SoundClass).map((value) => ({
  label: `${value} (${SoundClassLabels[value]})`,
  value
}));

const TIRE_WET_GRIP_CLASS_FILTER_OPTIONS: FilterOption<EnergyClassEU>[] = Object.values(WetGripClass).map((value) => ({
  label: `${value} - ${WetGripClassLabels[value]}`,
  value
}));

/** Minimum load index (`loadIndexGte`), labeled with its capacity. */
const TIRE_LOAD_INDEX_GTE_FILTER_OPTIONS: FilterOption<TireLoadIndex>[] = Object.values(TireLoadIndex)
  .filter((value): value is TireLoadIndex => typeof value === 'number')
  .map((value) => ({
    label: `${value} - ${TireLoadIndexLabels[value]} kg`,
    value
  }));

/** Minimum speed index (`speedIndexGte`), labeled with its speed. */
const TIRE_SPEED_INDEX_GTE_FILTER_OPTIONS: FilterOption<TireSpeedIndex>[] = Object.values(TireSpeedIndex).map(
  (value) => ({
    label: `${value} - ${TireSpeedIndexLabels[value]} km/h`,
    value
  })
);

const TIRE_SUPPLIER_PRODUCT_STATUS_FILTER_OPTIONS: FilterOption<SupplierProductStatus>[] =
  SUPPLIER_PRODUCT_STATUS_OPTIONS.map((status) => ({
    label: formatConstantCaseToSentenceCase(status.value),
    value: status.value
  }));

/** Tire brands as filter options. For `unit.multiSelect`. */
const useTireBrandOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedTireBrands,
    queryConfig: { staleTime: Infinity, select: toTireBrandOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** Tire shop suppliers as filter options (tire and rim inventory). For `unit.multiSelect`. */
export const useTireShopSupplierOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getTireShopSuppliers,
    queryConfig: { staleTime: Infinity, select: toTireShopSupplierOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** Supplier warehouses as filter options. For `unit.multiSelect`. */
const useSupplierWarehouseOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getSupplierWarehouses,
    queryConfig: { staleTime: Infinity, select: toSupplierWarehouseOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** Rim diameters (inch, with mm) from the car tire specifications as filter options. For `unit.multiSelect`. */
const useTireDimensionRimInchOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getCarSpecificationTireFilters,
    queryConfig: { staleTime: Infinity, select: toTireDimensionRimInchOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** Tire widths (mm) from the car tire specifications as filter options. For `unit.multiSelect`. */
const useTireDimensionWidthMmOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getCarSpecificationTireFilters,
    queryConfig: { staleTime: Infinity, select: toTireDimensionWidthMmOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** Tire profiles from the car tire specifications as filter options. For `unit.multiSelect`. */
const useTireDimensionProfileOptions = (): FilterOptionSource<number> => {
  const query = useFilterOptionsGet({
    type: URLKeys.getCarSpecificationTireFilters,
    queryConfig: { staleTime: Infinity, select: toTireDimensionProfileOptions }
  });

  return toFilterOptionSource(query, 'number');
};

/** The tire list's filters minus its own dimension: the dimension facet lists the sizes of that set. */
type TireInventoryDimensionScope = Omit<
  NonNullable<ShopTireInventoryDimensionsListApiData['query']>,
  'pageIndex' | 'pageSize' | 'dimension' | 'excludedDimension'
>;

/** Tire dimensions in the filtered inventory, with counts, as filter options. For `unit.multiSelect`. */
const useTireInventoryDimensionOptions = (scope: TireInventoryDimensionScope): FilterOptionSource<string> => {
  const { pageIndex, pageSize } = usePagination({ initialPageSize: 50 });

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedTireInventoryDimensions,
    input: { ...scope, pageIndex, pageSize },
    queryConfig: { staleTime: Infinity, select: toTireInventoryDimensionOptions }
  });

  return toFilterOptionSource(query, 'string');
};

/** Tire inventory filters, with facet counts; spread `apiInput` into the list query. */
export const useTireInventoryFilters = ({
  catalogChannelLock,
  oeMarksFitCarIds
}: {
  catalogChannelLock?: CatalogChannelLock;
  /** Cars the tires are offered for: hides tires OE-marked for another make, like an automatic offer. */
  oeMarksFitCarIds?: number[];
} = {}) => {
  const brands = useTireBrandOptions();
  const suppliers = useTireShopSupplierOptions();
  const warehouses = useSupplierWarehouseOptions();
  const rimInches = useTireDimensionRimInchOptions();
  const widths = useTireDimensionWidthMmOptions();
  const profiles = useTireDimensionProfileOptions();
  const oemCatalogNodes = useVehicleCatalogNodeOptions();

  const lockedCatalogChannels = lockedCatalogChannelQuery(catalogChannelLock);
  // Split around the dimension facet, which lists the sizes of the set every other filter leaves.
  const leading = useFilterUnits([
    tireAxleFitmentsFilter,
    supplierProductStatusFilter,
    // A locked channel is forced and its control hidden; while the lock is pending the list waits.
    lockedCatalogChannels
      ? unit.locked(lockedCatalogChannels)
      : isCatalogChannelFilterHidden(catalogChannelLock)
        ? unit.locked({})
        : catalogChannelFilter,
    unit.locked({ originalEquipmentMarksFitCarIds: oeMarksFitCarIds })
  ]);
  const trailing = useFilterUnits([
    inStockFilter,
    unit.multiSelect('supplierIds', { title: 'Suppliers', source: suppliers, exclude: true }),
    unit.multiSelect('warehouseIds', {
      title: 'Warehouses',
      description: 'Filter to tires stored in any of the given warehouses.',
      source: warehouses,
      exclude: true
    }),
    unit.multiSelect('brandIds', { title: 'Brands', source: brands, exclude: true }),
    classificationFilter,
    seasonFilter,
    unit.multiSelect('dimensionRimInchIn', {
      title: 'Rim Diameter',
      description: 'The diameter of the rim in inches.',
      source: rimInches,
      exclude: true
    }),
    unit.multiSelect('dimensionWidthMmIn', {
      title: 'Tire Width',
      description: 'The width of the tire in millimeters.',
      source: widths,
      exclude: true
    }),
    unit.multiSelect('dimensionProfileIn', {
      title: 'Tire Profile',
      description: 'The aspect ratio (height to width ratio) of the tire.',
      source: profiles,
      exclude: true
    }),
    loadIndexFilter,
    speedIndexFilter,
    euClassFilter,
    soundClassFilter,
    isIceGripFilter,
    wetGripFilter,
    isRunFlatFilter,
    extraLoadFilter,
    isStuddedFilter,
    originalEquipmentMarkFilter(
      unit.multiSelect('originalEquipmentMarkCatalogNodeIds', {
        title: 'Car (OEM)',
        description: 'Filter by the catalog make or model the tire original equipment mark is for',
        source: oemCatalogNodes,
        defaultVisibleOptions: 5,
        exclude: true
      })
    ),
    priceFilter
  ]);

  const dimensions = useTireInventoryDimensionOptions({ ...leading.apiInput, ...trailing.apiInput });
  const filters = useFilterUnits([leading, dimensionFilter(dimensions), trailing]);

  const { data: filterCounts, error: filterCountsError } = useFilterOptionsGet({
    type: URLKeys.getTireInventoryFilterCounts,
    input: filters.apiInput,
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });
  // Facet counts are secondary — list still works if filter-counts fails.
  void filterCountsError;

  // Keyed by control id, which is the param the control writes.
  const countMap = {
    supplierProductStatuses: filterCounts?.supplierProductStatuses,
    catalogChannels: filterCounts?.catalogChannels,
    inStock: filterCounts?.inStock,
    supplierIds: filterCounts?.suppliers,
    warehouseIds: filterCounts?.warehouses,
    brandIds: filterCounts?.brands,
    classification: filterCounts?.classifications,
    season: filterCounts?.seasons,
    dimensionRimInchIn: filterCounts?.dimensionRimInch,
    dimensionWidthMmIn: filterCounts?.dimensionWidthMm,
    dimensionProfileIn: filterCounts?.dimensionProfile,
    loadIndexGte: filterCounts?.loadIndexes,
    speedIndexGte: filterCounts?.speedIndexes,
    classEu: filterCounts?.classEu,
    soundClass: filterCounts?.soundClasses,
    wetGripClass: filterCounts?.wetGripClasses,
    isIceGrip: filterCounts?.isIceGrip,
    isRunFlat: filterCounts?.isRunFlat,
    extraLoad: filterCounts?.extraLoad,
    isStudded: filterCounts?.isStudded,
    hasNoOriginalEquipmentMarks: filterCounts?.hasNoOriginalEquipmentMarks,
    originalEquipmentMarkCatalogNodeIds: filterCounts?.originalEquipmentMarkCatalogNodes
  };

  return mapFilterControls(filters, (control) => enrichFiltersWithCounts([control], countMap)[0] ?? control);
};

/** What a link into the tire inventory list may set (`filterSearch`). */
export type TireInventoryListLink = FilterLinkOf<typeof useTireInventoryFilters>;

/**
 * Search string that opens `useTireInventoryFilters` on these values, for a list whose params live
 * in a `NavioLocalQueryParamProvider`. Starting there spares a first fetch of the unfiltered list.
 */
export const getTireInventoryFilterSearch = (filters: InitialTireInventoryFilters) =>
  filterSearch<TireInventoryListLink>({
    dimensionWidthMmIn: filters.dimensionWidthMm,
    dimensionProfileIn: filters.dimensionProfile,
    dimensionRimInchIn: filters.dimensionRimInch,
    season: filters.season,
    supplierProductStatuses: filters.supplierProductStatuses,
    inStock: filters.inStock
  });

const unit = tireInventoryFilterUnit;

const supplierProductStatusFilter = unit.multiSelect('supplierProductStatuses', {
  title: 'Supplier Product Status',
  options: TIRE_SUPPLIER_PRODUCT_STATUS_FILTER_OPTIONS,
  defaults: [SupplierProductStatus.AVAILABLE],
  exclude: true
});

const catalogChannelFilter = unit.multiSelect('catalogChannels', {
  title: 'Catalog channel',
  description: 'How the tire is sourced (EDI feed, adapter resale, or manual)',
  options: INVENTORY_CATALOG_CHANNEL_OPTIONS,
  exclude: true
});

const inStockFilter = unit.boolean('inStock', {
  title: 'In Stock',
  description: 'Indicates that the tire is in stock',
  defaultValue: true
});

const classificationFilter = unit.multiSelect('classification', {
  title: 'Classification',
  options: TIRE_CLASSIFICATION_OPTIONS,
  exclude: true
});

const seasonFilter = unit.multiSelect('season', {
  title: 'Seasons',
  description: 'Select the seasons when these tires are suitable for use',
  options: TIRE_SEASON_OPTIONS,
  exclude: true
});

const euClassFilter = unit.multiSelect('classEu', {
  title: 'EU Class',
  description: 'European Union labeling class summarizing rolling resistance, wet grip, and noise level.',
  options: TIRE_EU_CLASS_FILTER_OPTIONS,
  exclude: true
});

const soundClassFilter = unit.multiSelect('soundClass', {
  title: 'Sound Class',
  description: 'Indicates the external noise level the tire generates, rated in decibels.',
  options: TIRE_SOUND_CLASS_FILTER_OPTIONS,
  exclude: true
});

const wetGripFilter = unit.multiSelect('wetGripClass', {
  title: 'Wet Grip Class',
  description: 'Filter by wet road performance rating',
  options: TIRE_WET_GRIP_CLASS_FILTER_OPTIONS,
  exclude: true
});

const isIceGripFilter = unit.boolean('isIceGrip', {
  title: 'Ice Grip',
  description: 'Indicates that the tire has enhanced grip on icy surfaces (typically for Nordic winter tires).'
});

const isRunFlatFilter = unit.boolean('isRunFlat', {
  title: 'Run Flat',
  description: 'Specifies if the tire can be driven for a short distance after a puncture or loss of air pressure.'
});

const extraLoadFilter = unit.boolean('extraLoad', {
  title: 'Extra Load',
  description: 'Specifies if the tire is reinforced to carry heavier loads at higher inflation pressures.'
});

const isStuddedFilter = unit.boolean('isStudded', {
  title: 'Studded',
  description: 'Shows whether the tire is equipped with metal studs for improved traction on ice.'
});

// Entered in kroner, sent in minor units (øre) to the list, its facet counts and the dimension facet alike.
const priceFilter = unit.numberRange('salesItemPriceNet', {
  title: 'Price',
  description: 'Filter by price range',
  minLabel: 'Min price',
  maxLabel: 'Max price',
  money: true,
  exclude: true
});

// Index params carry index semantics: the backend converts the load index code to its kg capacity and the speed
// symbol to km/h. The control picks a minimum and its Exclude drops exact indexes, two differently named params.
const loadIndex = singleWithExclusions({
  key: 'loadIndexGte',
  excludedKey: 'excludedLoadIndex',
  options: TIRE_LOAD_INDEX_GTE_FILTER_OPTIONS,
  text: {
    title: 'Minimum load index (all axles)',
    description: 'Numerical code indicating the maximum weight a tire can carry at its rated speed.'
  }
});

const loadIndexFilter = unit.compose('loadIndex', loadIndex.urlParams, (values, setValues) => {
  const { control, include, exclude } = loadIndex.resolve(values, setValues);
  return { controls: control, params: { loadIndexGte: include, excludedLoadIndex: exclude } };
});

const speedIndex = singleWithExclusions({
  key: 'speedIndexGte',
  excludedKey: 'excludedSpeedIndex',
  options: TIRE_SPEED_INDEX_GTE_FILTER_OPTIONS,
  text: {
    title: 'Minimum speed index (all axles)',
    description: 'The maximum speed the tire can safely sustain, represented by a letter (e.g. H = 210 km/h).'
  }
});

const speedIndexFilter = unit.compose('speedIndex', speedIndex.urlParams, (values, setValues) => {
  const { control, include, exclude } = speedIndex.resolve(values, setValues);
  return { controls: control, params: { speedIndexGte: include, excludedSpeedIndex: exclude } };
});

const dimensionFilter = (source: FilterOptionSource<string>) =>
  unit.multiSelect('dimension', {
    title: 'Dimensions',
    description: 'Tire dimension (e.g. 195/65R15)',
    source,
    exclude: true
  });

const HAS_NO_OEM_URL_KEY = filterUrlState.urlKey('hasNoOriginalEquipmentMarks');

type OemCatalogParams = Pick<
  TireInventoryFilterInput,
  'originalEquipmentMarkCatalogNodeIds' | 'excludedOriginalEquipmentMarkCatalogNodeIds'
>;

/**
 * "No OEM connections" and the OEM picker. Turning it on clears the picker and hides it, so its params
 * (exclusions too) go with it: one click writes both, so one unit, the picker resolved inside it.
 */
const originalEquipmentMarkFilter = (cars: FilterSpec<OemCatalogParams>) =>
  unit.compose<Pick<TireInventoryFilterInput, 'hasNoOriginalEquipmentMarks'> & OemCatalogParams>(
    'originalEquipmentMarks',
    { [HAS_NO_OEM_URL_KEY]: filterUrlState.boolean, ...cars.urlParams },
    (values, setValues) => {
      const hasNone = (values[HAS_NO_OEM_URL_KEY] as boolean | null | undefined) ?? undefined;
      const clearedPickers = Object.fromEntries(Object.keys(cars.urlParams).map((urlKey) => [urlKey, undefined]));
      const hasNoneControl: FilterControl = {
        id: 'hasNoOriginalEquipmentMarks',
        type: 'boolean',
        title: 'No OEM connections',
        description: 'Show tires that have no original equipment mark connections',
        value: hasNone ?? null,
        onChange: (value) =>
          setValues(() => ({ [HAS_NO_OEM_URL_KEY]: value ?? undefined, ...(value === true && clearedPickers) })),
        reset: () => setValues(() => ({ [HAS_NO_OEM_URL_KEY]: undefined })),
        isInitial: values[HAS_NO_OEM_URL_KEY] === undefined
      };
      if (hasNone === true) {
        return { controls: hasNoneControl, params: { hasNoOriginalEquipmentMarks: true } };
      }
      const carsUnit = cars.resolve(values, setValues);
      return {
        controls: [hasNoneControl, ...carsUnit.controls],
        params: { hasNoOriginalEquipmentMarks: hasNone, ...carsUnit.apiInput }
      };
    }
  );

const toTireBrandOptions = ({ results }: ShopTireBrandListApiResponse) =>
  results.map((brand) => ({ label: brand.name, value: brand.id, imageUrl: brand.logo?.url }));

const toTireShopSupplierOptions = ({ results }: ShopSupplierListApiResponse) =>
  results.map((supplier) => ({ label: supplier.name, value: supplier.id }));

const toSupplierWarehouseOptions = ({ results }: SupplierWarehouseListApiResponse) =>
  results.map((warehouse) => ({ label: warehouse.name, value: warehouse.id }));

const toTireDimensionRimInchOptions = ({ dimensionsRimInch }: CarFiltersListApiResponse) =>
  dimensionsRimInch.map((rim) => ({
    label: `${rim} inch (${(rim * INCH_TO_MM_CONVERSION).toFixed(2)} mm)`,
    value: rim
  }));

const toTireDimensionWidthMmOptions = ({ dimensionsWidthMm }: CarFiltersListApiResponse) =>
  dimensionsWidthMm.map((width) => ({ label: `${width} mm`, value: width }));

const toTireDimensionProfileOptions = ({ dimensionsProfile }: CarFiltersListApiResponse) =>
  dimensionsProfile.map((profile) => ({ label: `${profile}`, value: profile }));

const toTireInventoryDimensionOptions = ({ results }: ShopTireInventoryDimensionsListApiResponse) =>
  results.map((dimension) => ({ label: `${dimension.dimension} (${dimension.count})`, value: dimension.dimension }));
